/**
 * Service for interacting with Launch Darkly to retrieve feature flags
 */
import ldClient from 'ldclient-js';
import { v4 as uuidv4 } from 'uuid';

const publicFields = ['email', 'key', 'anonymous', 'name'];
let client;

/**
 * Transform the user data to be sent to Launch Darkly to identify a user
 *
 * @param {object} user - The identity information of the user
 * @returns {object} - User data mapped to specified identity fields
 */
const formatUserData = (user) => {
  const mappedFields = {
    anonymous: !user.id,
    key: user.id || uuidv4(),
  };
  return { ...user, ...mappedFields };
};

/**
 * Examines the user object's keys and returns a list of keys that are note
 * part of the list of public keys.
 *
 * @param {object} user - The identity information of the user
 * @param {array} pubFields - Array of string representing the public field names
 * @returns {array} - Array of field names on the user that are not part of
 *  the public fields list
 */
const getPrivateAttrsList = (user, pubFields) => {
  const allFields = Object.keys(user);
  return allFields.filter((field) => !pubFields.includes(field));
};

/**
 * Initialize connection to Launch Darkly to get feature flags
 *
 * @param {object} user - The identity information of the user for which to load
 *   feature flags for.
 * @param {object} cachedFlags - An object containing any cached feature flags
 *   that can be used to bootstrap the app until the actual flags load.
 * @returns {function} - Function that resolves a Promise when flags are ready
 */
const initialize = (user, cachedFlags, privateAttrs) => (resolve) => {
  client = ldClient.initialize(process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID, user, {
    bootstrap: cachedFlags,
    privateAttributeNames: privateAttrs,
  });
  client.on('ready', () => {
    resolve(client.allFlags());
  });
};

/**
 * Update the identity of the user
 *
 * @param {object} user - The identity information of the user for which to load
 *   feature flags for.
 * @returns {Promise} - Promise resolved with all of the user's feature flags
 */
const identify = (user) => (resolve) => {
  client.identify(user, null, () => {
    resolve(client.allFlags());
  });
};

/**
 * Get the flags for a given user
 *
 * @param {object} user - The identity information of the user for which to load
 *   feature flags for.
 * @param {object} cachedFlags - An object containing any cached feature flags
 *   that can be used to bootstrap the app until the actual flags load.
 * @returns {Promise} - Promise resolved with all of the user's feature flags
 */
const getFlags = (user, cachedFlags = {}) => {
  const userData = formatUserData(user || {});
  const privateAttrs = getPrivateAttrsList(userData, publicFields);
  if (!client) {
    return new Promise(initialize(userData, cachedFlags, privateAttrs));
  }
  return new Promise(identify(userData));
};

/**
 * Adds the given callback as a change handler to be called when feature flag
 * updates occur
 *
 * @param {function} onChangeCallback - Callback function to be removed as a
 *  change handler
 */
const subscribe = (onChangeCallback) => {
  if (!client) {
    return;
  }
  client.on('change', onChangeCallback);
};

/**
 * Removes the given callback as a change handler when feature flag updates
 * occur
 *
 * @param {function} onChangeCallback - Callback function to be removed as a
 *  change handler
 */
const unsubscribe = (onChangeCallback) => {
  if (!client) {
    return;
  }
  client.off('change', onChangeCallback);
};

export default {
  getFlags,
  subscribe,
  unsubscribe,
};
